import React, { useCallback, useEffect, useState, useRef } from 'react';
import PageHeader from './components/PageHeader';
import Pagination from './components/Pagination';
import IconButton from './components/IconButton';
import EditActionIcon from './components/EditActionIcon';
import DownloadActionIcon from './components/DownloadActionIcon';
import DownloadPdfActionIcon from './components/DownloadPdfActionIcon';
import SortableTableHeader from './components/SortableTableHeader';
import exportPrintRunJob from "./utils/exportPrintRunJob";
import downloadPrintRunOutput from './utils/downloadPrintRunOutput';
import usePrintRunsPageResults from './hooks/usePrintRunsPageResults';
import { useUrlFilters, useUrlParam } from './hooks/urlParams';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from './constants';
import { getNextSortCriterion, type FieldSortCriterion } from './utils/tableSort';
import useMultiPackageRecoveryHistory from './hooks/useMultiPackageRecoveryHistory';
import { isMultiPackageLabelGenerationEnabled } from './features/multiPackagePrint/capabilities';
import DialogAlert from './components/DialogAlert';
import { isAwaitingMultiPackageOutput, multiPackageItemStatusLabel } from './features/multiPackagePrint/multiPackageStatus';

function resolvePrintRunProductName(job: any): string {
  return (
    job?.productOverrideName ||
    job?.productName ||
    ''
  );
}

export default function PrintRunsPage({ organizationId, currentUser, onAdd, onEdit }) {

  const ALL_STATUS = "all";
  const descendingFirstFields = useRef(new Set(['printedDate']));

  const [filters, setFilters] = useUrlFilters<{
    status: string;
    searchTerm: string;
  }>({
    status: { default: ALL_STATUS, type: 'string' },
    searchTerm: { default: '', type: 'string' },
  });
  const [csvInFlight, setCsvInFlight] = useState<Record<string, boolean>>({});
  const [pdfInFlight, setPdfInFlight] = useState<Record<string, boolean>>({});
  const csvInFlightRef = useRef<Record<string, boolean>>({});
  const pdfInFlightRef = useRef<Record<string, boolean>>({});
  const isMountedRef = useRef(true);
  const [pageParam, setPageParam] = useUrlParam<number>('page', 0);
  const [pageSizeParam, setPageSizeParam] = useUrlParam<number>('pageSize', DEFAULT_PAGE_SIZE, {
    validate: (value) => PAGE_SIZE_OPTIONS.includes(value as (typeof PAGE_SIZE_OPTIONS)[number])
      ? value
      : undefined,
  });
  const [pagePending, setPagePending] = useState(pageParam);
  const [sort, setSort] = useState<FieldSortCriterion>(null);
  const [resultsOrganizationId, setResultsOrganizationId] = useState(organizationId);

  if (resultsOrganizationId !== organizationId) {
    setResultsOrganizationId(organizationId);
    setPageParam(0);
    setPagePending(0);
  }

  const recoveryEnabled = isMultiPackageLabelGenerationEnabled();
  const canWritePrintRuns = currentUser?.roleActions?.includes('WritePrintRuns')
    || currentUser?.roleActions?.includes('AdminPrintRuns');
  const canWritePackages = currentUser?.roleActions?.includes('WritePackages')
    || currentUser?.roleActions?.includes('AdminPackages');
  const canResumeRecovery = currentUser?.isSuperUser || (canWritePrintRuns && canWritePackages);
  const {
    batches: recoveryBatches,
    loading: recoveryLoading,
    error: recoveryError,
    actionError: recoveryActionError,
    resumeBatch,
    downloadRecoveredBatch,
    refresh: refreshRecovery,
  } = useMultiPackageRecoveryHistory(organizationId, recoveryEnabled, !!canResumeRecovery);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const runInFlight = async (
    setMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
    mapRef: React.MutableRefObject<Record<string, boolean>>,
    job: any,
    action: () => Promise<unknown>,
  ) => {
    const key = String(job.id);
    if (mapRef.current[key]) return;
    mapRef.current = { ...mapRef.current, [key]: true };
    setMap((prev) => ({ ...prev, [key]: true }));
    try {
      await action();
    } finally {
      if (isMountedRef.current) {
        const { [key]: _refEntry, ...nextRef } = mapRef.current;
        mapRef.current = nextRef;
        setMap(({ [key]: _, ...rest }) => rest);
      }
    }
  };

  const handleExportCsv = (job) =>
    runInFlight(setCsvInFlight, csvInFlightRef, job, () => exportPrintRunJob(job));

  const handleDownloadOutput = (job) =>
    runInFlight(setPdfInFlight, pdfInFlightRef, job, () => downloadPrintRunOutput(organizationId, job));

  useEffect(() => {
    setPagePending(pageParam);
  }, [pageParam]);

  const { results, loading } = usePrintRunsPageResults({
    organizationId,
    page: pagePending,
    pageSize: pageSizeParam,
    filters,
    sort,
  });

  useEffect(() => {
    setPageParam(results.page);
  }, [results.page, setPageParam]);

  const toggleSortDirection = useCallback((fieldName: string) => {
    setSort((current) => getNextSortCriterion(current, fieldName, descendingFirstFields.current));
    setPagePending(0);
  }, []);

  const applyFilters = (updater) => {
    setPagePending(0);
    setFilters(updater);
  };
  
  return (
    <div className="stack" data-testid="page-prints">
      <PageHeader
        title="Label Prints"
        subtitle="Recent Jobs"
      />

      {recoveryEnabled && (recoveryBatches.length > 0 || recoveryError || recoveryActionError) && (
        <div className="card" data-testid="multi-package-recovery-history">
          <h2>Multi-package prints to finish</h2>
          <DialogAlert show={recoveryError} message="Recovery history could not be loaded. Try again." />
          <DialogAlert show={Boolean(recoveryActionError)} message={recoveryActionError ?? undefined} />
          {recoveryError && <button type="button" className="button secondary" disabled={recoveryLoading}
            onClick={() => void refreshRecovery()}>Retry recovery history</button>}
          {recoveryBatches.map((batch) => {
            const incompleteCount = batch.items.filter((item) => item.status !== 'Completed').length;
            const needsResume = batch.items.some((item) => item.status !== 'Completed' && !isAwaitingMultiPackageOutput(item));
            const readyCount = batch.outputManifest.items.length;
            return (
              <div className="filter-bar" key={batch.batchId} data-testid={`recovery-batch-${batch.batchId}`}>
                <div>
                  <strong>{batch.sourcePackageLabel || 'Source package'}</strong>
                  <div className="muted">
                    {incompleteCount} {incompleteCount === 1 ? 'package' : 'packages'} incomplete
                    {' · '}{readyCount} {readyCount === 1 ? 'package' : 'packages'} ready for output
                    {' · '}{new Date(batch.createdAt).toLocaleDateString()}
                    {' · '}Batch {batch.batchId}
                    <ul>{batch.items.filter((item) => item.status !== 'Completed').map((item) => (
                      <li key={`${item.metrcPackageId}:${item.sequence}`}>{item.packageLabel || `METRC package ${item.metrcPackageId}`} — {multiPackageItemStatusLabel(item)}</li>
                    ))}</ul>
                  </div>
                </div>
                {needsResume && <button
                  className="button cta"
                  disabled={recoveryLoading || !canResumeRecovery}
                  onClick={() => void resumeBatch(batch.batchId)}
                  data-testid={`resume-batch-${batch.batchId}`}
                >
                  {recoveryLoading ? 'Resuming...' : canResumeRecovery ? 'Resume' : 'Resume requires write access'}
                </button>}
                {batch.outputManifest.items.length > 0 && (
                  <button className="button secondary" disabled={recoveryLoading || !canResumeRecovery}
                    onClick={() => void downloadRecoveredBatch(batch)}>
                    Download completed package ZIP
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="card filter-card">
        <div className="filter-bar">
          <div className="filter-left">
            <input
              className="input filter-search"
              type="search"
              placeholder="Search label prints"
              value={filters.searchTerm}
              onChange={(e) => {
                applyFilters((current) => ({ ...current, searchTerm: e.target.value }));
              }}
              data-testid="prints-search"
            />
            <select
              className="select"
              value={filters.status}
              onChange={(e) => {
                applyFilters((current) => ({ ...current, status: e.target.value }));
              }}
              data-testid="prints-filter-status"
            >
              <option value="all">All statuses</option>
              <option value="0">Queued</option>
              <option value="1">In Progress</option>
              <option value="2">Complete</option>
              <option value="3">Failed</option>
              <option value="4">Draft</option>
            </select>
          </div>
          <div className="filter-right">
            {(currentUser?.roleActions?.indexOf('WritePrintRuns') >= 0 ||
              currentUser?.roleActions?.indexOf('AdminPrintRuns') >= 0) && <button className="button cta" onClick={onAdd} data-testid="prints-add">
              <span className="plus" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round"
                        strokeLinejoin="round"/>
                  <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round"
                        strokeLinejoin="round"/>
                </svg>
              </span>
              Start Print
            </button>}
          </div>
        </div>
      </div>

      <div className="card" data-testid="prints-table">
        <table className="table" data-testid="prints-table-content">
          <thead>
          <tr>
            <SortableTableHeader label="Job ID" fieldName="jobId" sort={sort} onSort={toggleSortDirection} />
            <SortableTableHeader label="Job Name" fieldName="jobName" sort={sort} onSort={toggleSortDirection} />
            <SortableTableHeader label="Template" fieldName="templateName" sort={sort} onSort={toggleSortDirection} />
            <SortableTableHeader label="Package" fieldName="packageLabel" sort={sort} onSort={toggleSortDirection} />
            <SortableTableHeader label="Product" fieldName="productName" sort={sort} onSort={toggleSortDirection} />
            <SortableTableHeader label="Count" fieldName="count" sort={sort} onSort={toggleSortDirection} />
            {/*<th>Status</th>*/}
            <SortableTableHeader label="Print Date" fieldName="printedDate" sort={sort} onSort={toggleSortDirection} />
            <th className="col-actions" aria-label="actions"/>
          </tr>
          </thead>
          <tbody>
            {results.data.map((job) => (
              <tr key={job.id} data-testid={`print-row-${job.id}`}>
                <td>{(job.status === 4 || job.status === 'Draft') ? `[DRAFT] ${job.jobId || '—'}` : (job.jobId || '—')}</td>
                <td>{job.jobName}</td>
                <td>{job.templateName || '—'}</td>
                <td>{job.packageLabel || '—'}</td>
                <td>{resolvePrintRunProductName(job) || '—'}</td>
                <td>{job.count}</td>
                {/*<td>{job.status}</td>*/}
                <td>{job.printedDate ? new Date(job.printedDate).toLocaleDateString() : ((job.status === 4 || job.status === 'Draft') ? '—' : null)}</td>
                <td style={{ textAlign: 'right' }}>
                  <div className="print-run-actions">
                    {(currentUser?.roleActions?.indexOf('WritePrintRuns') >= 0 ||
                      currentUser?.roleActions?.indexOf('AdminPrintRuns') >= 0) && <IconButton title="Edit" ariaLabel={`Edit ${job.name || 'print run'}`} onClick={() => onEdit && onEdit(job)} testId={`print-edit-${job.id}`}>
                      <EditActionIcon className="print-run-edit-icon" />
                    </IconButton>}

                    <IconButton
                      title={csvInFlight[job.id] ? 'Exporting...' : 'Export To CSV'}
                      ariaLabel={`Export ${job.jobName || 'print run'}`}
                      disabled={!!csvInFlight[job.id]}
                      onClick={() => handleExportCsv(job)}
                      testId={`print-download-${job.id}`}>
                      <DownloadActionIcon/>
                    </IconButton>

                    <IconButton
                      title={pdfInFlight[job.id] ? 'Generating Labels...' : 'Download Labels'}
                      ariaLabel={`Download labels for ${job.jobName || 'print run'}`}
                      disabled={!!pdfInFlight[job.id]}
                      onClick={() => handleDownloadOutput(job)}
                      testId={`print-download-pdf-${job.id}`}>
                      <DownloadPdfActionIcon/>
                    </IconButton>


                  </div>
              </td>
            </tr>
          ))}
          {results.total === 0 && (
            <tr data-testid="prints-results">
              <td colSpan={8} style={{textAlign: 'center', padding: '12px 0'}} className="muted">No label prints yet.
              </td>
            </tr>
          )}
          </tbody>
        </table>

        <Pagination
          page={results.page}
          totalPages={Math.ceil(results.total / results.pageSize)}
          onPrev={() => setPagePending(results.page - 1)}
          onNext={() => setPagePending(results.page + 1)}
          format="of"
          prevLabel="◀"
          nextLabel="▶"
          prevDisabled={loading}
          nextDisabled={loading}
          style={{ marginTop: 8 }}
          pageSize={results.pageSize}
          pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
          testId="prints-pagination"
          onPageSizeChange={(size) => {
            setPageSizeParam(size);
            setPagePending(0);
          }}
        />
      </div>
    </div>
  );
}
